import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

export interface NotificationEvent {
  type: string
  payload: Record<string, unknown>
  created_at: string
}

export interface NotificationDelivery {
  id: string
  event_id: string
  status: 'unread' | 'read' | 'archived'
  read_at: string | null
  created_at: string
  event: NotificationEvent | null
}

export interface Broadcast {
  id: string
  type: string
  title: string
  title_zh: string | null
  content: string | null
  content_zh: string | null
  display_type: 'banner' | 'modal' | 'message_center'
  priority: number
  starts_at: string
  ends_at: string | null
  is_active?: boolean
  audience_type?: 'all' | 'authenticated' | 'admins' | 'team'
  audience_team_id?: string | null
  metadata: Record<string, unknown>
  created_by?: string | null
  created_at?: string
}

const NOTIFICATIONS_PAGE_SIZE = 50

export function useNotifications(userId: string | null | undefined) {
  const queryClient = useQueryClient()

  const notificationsQuery = useInfiniteQuery({
    queryKey: ['notifications', userId],
    queryFn: async ({ pageParam }) => {
      if (!userId) return []

      const offset = typeof pageParam === 'number' ? pageParam : 0
      const { data, error } = await supabase
        .from('notification_deliveries')
        .select(`
          id,
          event_id,
          status,
          read_at,
          created_at,
          event:notification_events(type, payload, created_at)
        `)
        .eq('user_id', userId)
        .eq('channel', 'in_app')
        .order('created_at', { ascending: false })
        .range(offset, offset + NOTIFICATIONS_PAGE_SIZE - 1)

      if (error) throw error
      return (data || []).map(item => ({
        ...item,
        event: Array.isArray(item.event) ? item.event[0] || null : item.event,
      })) as NotificationDelivery[]
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage || lastPage.length < NOTIFICATIONS_PAGE_SIZE) return undefined
      return pages.length * NOTIFICATIONS_PAGE_SIZE
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  })

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', userId, 'unreadCount'],
    queryFn: async () => {
      if (!userId) return 0

      const { count, error } = await supabase
        .from('notification_deliveries')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('channel', 'in_app')
        .eq('status', 'unread')

      if (error) throw error
      return count ?? 0
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  })

  const notifications = notificationsQuery.data?.pages.flat() ?? []
  const isLoading = notificationsQuery.isLoading
  const error = notificationsQuery.error

  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notification_deliveries')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .eq('id', notificationId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
    },
  })

  const markAsUnread = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notification_deliveries')
        .update({ status: 'unread', read_at: null })
        .eq('id', notificationId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
    },
  })

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      if (!userId) return

      const { error } = await supabase
        .from('notification_deliveries')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('status', 'unread')

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
    },
  })

  const archiveNotifications = useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return
      const { error } = await supabase
        .from('notification_deliveries')
        .update({ status: 'archived', read_at: new Date().toISOString() })
        .in('id', ids)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
    },
  })

  const unarchiveNotifications = useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return
      const { error } = await supabase
        .from('notification_deliveries')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .in('id', ids)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
    },
  })

  const deleteNotifications = useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return
      const { error } = await supabase
        .from('notification_deliveries')
        .delete()
        .in('id', ids)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
    },
  })

  useEffect(() => {
    if (!userId) return

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notification_deliveries',
        filter: `user_id=eq.${userId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notification_deliveries',
        filter: `user_id=eq.${userId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'notification_deliveries',
        filter: `user_id=eq.${userId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['notifications', userId] })
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, queryClient])

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead: markAsRead.mutate,
    markAsUnread: markAsUnread.mutate,
    markAllAsRead: markAllAsRead.mutate,
    archiveNotifications: archiveNotifications.mutate,
    unarchiveNotifications: unarchiveNotifications.mutate,
    deleteNotifications: deleteNotifications.mutate,
    isMarkingAsRead: markAsRead.isPending,
    isMarkingAsUnread: markAsUnread.isPending,
    isMarkingAllAsRead: markAllAsRead.isPending,
    isArchiving: archiveNotifications.isPending,
    isUnarchiving: unarchiveNotifications.isPending,
    isDeleting: deleteNotifications.isPending,
    hasNextPage: notificationsQuery.hasNextPage ?? false,
    isFetchingNextPage: notificationsQuery.isFetchingNextPage,
    fetchNextPage: notificationsQuery.fetchNextPage,
    refetch: notificationsQuery.refetch,
    isRefetching: notificationsQuery.isRefetching,
  }
}

const DISMISSED_BROADCASTS_KEY = 'nexusos_dismissed_broadcasts'

function getLocalDismissedIds(): Set<string> {
  try {
    const stored = localStorage.getItem(DISMISSED_BROADCASTS_KEY)
    return stored ? new Set(JSON.parse(stored)) : new Set()
  } catch {
    return new Set()
  }
}

function addLocalDismissedId(broadcastId: string): void {
  try {
    const ids = getLocalDismissedIds()
    ids.add(broadcastId)
    localStorage.setItem(DISMISSED_BROADCASTS_KEY, JSON.stringify([...ids]))
  } catch {
    // localStorage unavailable
  }
}

export function useBroadcasts(userId: string | null | undefined, language: 'en' | 'zh' = 'en') {
  const queryClient = useQueryClient()
  const trackedEventKeys = useRef<Set<string>>(new Set())

  const { data: broadcasts = [], isLoading } = useQuery({
    queryKey: ['broadcasts', userId],
    queryFn: async () => {
      const { data: broadcastsData, error: broadcastsError } = await supabase
        .from('broadcasts')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })

      if (broadcastsError) throw broadcastsError

      const localDismissedIds = getLocalDismissedIds()

      if (!userId) {
        return (broadcastsData as Broadcast[]).filter(b => !localDismissedIds.has(b.id))
      }

      const { data: receipts, error: receiptsError } = await supabase
        .from('broadcast_receipts')
        .select('broadcast_id')
        .eq('user_id', userId)

      if (receiptsError) throw receiptsError

      const dismissedIds = new Set([
        ...(receipts?.map(r => r.broadcast_id) || []),
        ...localDismissedIds,
      ])
      return (broadcastsData as Broadcast[]).filter(b => !dismissedIds.has(b.id))
    },
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    trackedEventKeys.current = new Set()
  }, [userId])

  const trackBroadcastEvent = useMutation({
    mutationFn: async ({ broadcastId, eventType, metadata }: { broadcastId: string; eventType: 'view' | 'click'; metadata?: Record<string, unknown> }) => {
      if (!userId) return

      const { error } = await supabase
        .from('broadcast_events')
        .upsert(
          {
            broadcast_id: broadcastId,
            user_id: userId,
            event_type: eventType,
            metadata: metadata || {},
          },
          { onConflict: 'broadcast_id,user_id,event_type', ignoreDuplicates: true }
        )

      if (error) throw error
    },
  })

  const trackEvent = (broadcastId: string, eventType: 'view' | 'click', metadata?: Record<string, unknown>) => {
    if (!userId) return
    const key = `${broadcastId}:${eventType}`
    if (trackedEventKeys.current.has(key)) return
    trackedEventKeys.current.add(key)
    trackBroadcastEvent.mutate({ broadcastId, eventType, metadata })
  }

  const dismissBroadcast = useMutation({
    mutationFn: async (broadcastId: string) => {
      addLocalDismissedId(broadcastId)

      if (!userId) return

      const { error } = await supabase
        .from('broadcast_receipts')
        .upsert(
          { broadcast_id: broadcastId, user_id: userId },
          { onConflict: 'broadcast_id,user_id', ignoreDuplicates: true }
        )

      if (error) throw error
    },
    onMutate: async (broadcastId: string) => {
      await queryClient.cancelQueries({ queryKey: ['broadcasts', userId] })
      const previousBroadcasts = queryClient.getQueryData<Broadcast[]>(['broadcasts', userId])
      queryClient.setQueryData<Broadcast[]>(['broadcasts', userId], old =>
        old?.filter(b => b.id !== broadcastId) ?? []
      )
      return { previousBroadcasts }
    },
    onError: (_err, _broadcastId, context) => {
      if (context?.previousBroadcasts) {
        queryClient.setQueryData(['broadcasts', userId], context.previousBroadcasts)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcasts', userId] })
    },
  })

  useEffect(() => {
    if (!userId) return

    const channel = supabase
      .channel(`broadcast_receipts:${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'broadcast_receipts',
        filter: `user_id=eq.${userId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['broadcasts', userId] })
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, queryClient])

  const activeBanners = broadcasts.filter(b => b.display_type === 'banner')
  const activeModals = broadcasts.filter(b => b.display_type === 'modal')
  const messageCenter = broadcasts.filter(b => b.display_type === 'message_center')

  const getLocalizedContent = (broadcast: Broadcast) => ({
    title: (language === 'zh' && broadcast.title_zh) ? broadcast.title_zh : broadcast.title,
    content: (language === 'zh' && broadcast.content_zh) ? broadcast.content_zh : broadcast.content,
  })

  return {
    broadcasts,
    activeBanners,
    activeModals,
    messageCenter,
    isLoading,
    dismissBroadcast: dismissBroadcast.mutate,
    isDismissing: dismissBroadcast.isPending,
    getLocalizedContent,
    trackBroadcastEvent: trackEvent,
  }
}
